import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Globe, MapPin, ArrowLeft } from 'lucide-react';
import { OnboardingV2State } from '../../../../store/onboardingV2/types.ts';
import { JURISDICTIONS } from '../../../../constants/jurisdictions.ts';
import { useHorizontalDragScroll } from '../../../../hooks/onboardingV2/useHorizontalDragScroll.ts';
import SearchInput from '../SearchInput/SearchInput.tsx';
import styles from './WorkZonesSelector.module.scss';

interface WorkZonesSelectorProps {
  state: OnboardingV2State;
  onAction: (action: string, data?: any) => void;
}

const WorkZonesSelector: React.FC<WorkZonesSelectorProps> = ({ state, onAction }) => {
  const [selectedJurisdiction, setSelectedJurisdiction] = useState<number | null>(null);
  const [citySearchTerm, setCitySearchTerm] = useState('');
  const [lastAddedCityId, setLastAddedCityId] = useState<string | number | null>(null);
  const rawCoverageKind = state.workCoverageKind ?? 'specificZones';

  useEffect(() => {
    if (rawCoverageKind === 'mapRadius') {
      onAction('workZone', { action: 'specificZones' });
    }
  }, [rawCoverageKind, onAction]);

  const coverageKind = rawCoverageKind === 'mapRadius' ? 'specificZones' : rawCoverageKind;

  const jurisdictionsScroll = useHorizontalDragScroll([
    selectedJurisdiction,
    state.allCountry,
    coverageKind,
  ]);
  const citiesScroll = useHorizontalDragScroll([
    selectedJurisdiction,
    citySearchTerm,
    state.allCountry,
    coverageKind,
    state.cities,
  ]);

  const filteredCities = useMemo(() => {
    if (!state.cities) return [];
    if (!citySearchTerm) {
      return state.cities;
    }
    return state.cities.filter((city: any) =>
      city.name.toLowerCase().includes(citySearchTerm.toLowerCase())
    );
  }, [state.cities, citySearchTerm]);

  const isCompact = !state.allCountry;

  return (
    <motion.div
      className={styles.interactiveContainer}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className={styles.workZonesSection}>
        <div className={styles.workZoneHeaderRow}>
          {selectedJurisdiction && !state.allCountry && (
            <div className={styles.selectedCategoryHeader}>
              <button
                className={styles.backToCategories}
                onClick={() => {
                  setSelectedJurisdiction(null);
                  setCitySearchTerm('');
                }}
                type="button"
              >
                <ArrowLeft size={16} />
              </button>
              <span className={styles.selectedCategoryName}>
                {JURISDICTIONS.find((j) => j.id === selectedJurisdiction)?.name}
              </span>
            </div>
          )}
          <div className={styles.workZoneTypeAndContinueRow}>
            <div
              className={`${styles.workZoneTypeSelector} ${
                isCompact ? styles.workZoneTypeSelectorCompact : ''
              }`}
            >
            <motion.button
              className={`${styles.workZoneTypeOption} ${
                isCompact ? styles.workZoneTypeOptionCompact : ''
              } ${state.allCountry ? styles.active : ''}`}
              onClick={() => {
                setSelectedJurisdiction(null);
                setCitySearchTerm('');
                onAction('workZone', { action: 'allCountry' });
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {state.allCountry && !isCompact && (
                <span className={styles.completionBadge}>
                  <Check size={14} />
                </span>
              )}
              <Globe size={isCompact ? 20 : 32} />
              <div className={styles.workZoneTypeText}>
                <p
                  className={`${styles.workZoneTypeTitle} ${
                    isCompact ? styles.workZoneTypeTitleCompact : ''
                  }`}
                >
                  Todo el país
                </p>
                <p
                  className={`${styles.workZoneTypeSubtitle} ${
                    isCompact ? styles.workZoneTypeSubtitleCompact : ''
                  }`}
                >
                  Comercializo en toda Argentina
                </p>
              </div>
            </motion.button>

            <motion.button
              className={`${styles.workZoneTypeOption} ${
                isCompact ? styles.workZoneTypeOptionCompact : ''
              } ${!state.allCountry && coverageKind === 'specificZones' ? styles.active : ''}`}
              onClick={() => {
                setSelectedJurisdiction(null);
                setCitySearchTerm('');
                onAction('workZone', { action: 'specificZones' });
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {!state.allCountry &&
                coverageKind === 'specificZones' &&
                state.workZones.length > 0 &&
                !isCompact && (
                <span className={styles.completionBadge}>
                  <Check size={14} />
                </span>
              )}
              <MapPin size={isCompact ? 20 : 32} />
              <div className={styles.workZoneTypeText}>
                <p
                  className={`${styles.workZoneTypeTitle} ${
                    isCompact ? styles.workZoneTypeTitleCompact : ''
                  }`}
                >
                  Zonas específicas
                </p>
                <p
                  className={`${styles.workZoneTypeSubtitle} ${
                    isCompact ? styles.workZoneTypeSubtitleCompact : ''
                  }`}
                >
                  Seleccioná provincias y ciudades
                </p>
              </div>
            </motion.button>
          </div>
            {!selectedJurisdiction && (state.allCountry || state.workZones.length > 0) && (
              <motion.button
                className={styles.workZonesContinueButton}
                onClick={() => onAction('workZonesComplete')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="button"
              >
                Continuar
              </motion.button>
            )}
          </div>
        </div>

        {!state.allCountry && coverageKind === 'specificZones' && (
          <div className={styles.zonesSelector}>
            {!selectedJurisdiction && (
              <div
                className={styles.jurisdictionsList}
                ref={jurisdictionsScroll.ref}
                onMouseDown={jurisdictionsScroll.onMouseDown}
                onMouseMove={jurisdictionsScroll.onMouseMove}
                onMouseUp={jurisdictionsScroll.onMouseUp}
                onMouseLeave={jurisdictionsScroll.onMouseLeave}
              >
                {JURISDICTIONS.map((jurisdiction) => (
                  <motion.button
                    key={jurisdiction.id}
                    className={styles.jurisdictionPill}
                    onClick={() => {
                      setSelectedJurisdiction(jurisdiction.id);
                      setCitySearchTerm('');
                      onAction('selectJurisdiction', { jurisdictionId: jurisdiction.id });
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {jurisdiction.name}
                  </motion.button>
                ))}
              </div>
            )}

            {selectedJurisdiction && (
              <div className={styles.citiesSelector}>
                <SearchInput
                  value={citySearchTerm}
                  placeholder="Buscar ciudad..."
                  onChange={setCitySearchTerm}
                  onClear={() => setCitySearchTerm('')}
                  showClear={!!citySearchTerm}
                  showAction={state.workZones.length > 0}
                  actionLabel="Continuar"
                  onAction={() => onAction('workZonesComplete')}
                  containerClassName={styles.citiesSearch}
                  inputClassName={styles.citiesInput}
                  iconClassName={styles.searchIcon}
                  clearButtonClassName={styles.clearButton}
                  actionClassName={styles.skillsContinueButton}
                />

                {state.isLoading ? (
                  <motion.div
                    className={styles.loadingContainer}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <motion.div
                      className={styles.spinner}
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 0.8,
                        repeat: Infinity,
                        ease: 'linear',
                      }}
                    />
                    <motion.p
                      className={styles.loadingText}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      Cargando ciudades...
                    </motion.p>
                  </motion.div>
                ) : (
                  filteredCities.length > 0 && (
                    <div
                      className={styles.citiesList}
                      ref={citiesScroll.ref}
                      onMouseDown={citiesScroll.onMouseDown}
                      onMouseMove={citiesScroll.onMouseMove}
                      onMouseUp={citiesScroll.onMouseUp}
                      onMouseLeave={citiesScroll.onMouseLeave}
                    >
                    {filteredCities.map((city: any) => {
                        const cityKey = city.id ?? city.name;
                        const zoneIndex = state.workZones.findIndex(
                          (z: any) => z.jurisdiccion?.id === selectedJurisdiction && z.city === city.name
                        );
                        const zoneExists = zoneIndex !== -1;
                        const zoneId = zoneExists ? zoneIndex : null;
                        return (
                          <motion.button
                            key={cityKey}
                            className={`${styles.cityButton} ${
                              zoneExists ? styles.cityButtonActive : ''
                            } ${lastAddedCityId === cityKey ? styles.addedPulse : ''}`}
                            onClick={() => {
                              if (zoneExists) {
                                onAction('workZone', { action: 'remove', zoneId });
                                return;
                              }
                              const jurisdiction = JURISDICTIONS.find((j) => j.id === selectedJurisdiction);
                              if (jurisdiction) {
                                onAction('workZone', {
                                  action: 'add',
                                  zone: {
                                    id: city.id,
                                    name: city.name,
                                    jurisdiccion: { id: jurisdiction.id, name: jurisdiction.name },
                                    city: city.name,
                                  },
                                });
                                setCitySearchTerm('');
                                setLastAddedCityId(cityKey);
                                setTimeout(() => {
                                  setLastAddedCityId((prev) => (prev === cityKey ? null : prev));
                                }, 350);
                              }
                            }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            transition={{ duration: 0.32, ease: 'easeOut' }}
                          >
                            {zoneExists && <Check size={16} className={styles.selectedCheck} />}
                            {city.name}
                          </motion.button>
                        );
                      })}
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default WorkZonesSelector;
